import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import CurrencyInput from 'react-currency-input-field';
import Heading from '../../../../../generic-components/Heading';
import { LiliTransaction } from '../../../../TransactionsFacelift/types/lili-transaction';
import { CircleWrapper } from './GenericComponents';
import { ReactComponent as IconActionBusiness } from '../../../../../assets/icons/svg/colored-icons/ic-colored-categorization-business.svg';
import { ReactComponent as IconActionPersonal } from '../../../../../assets/icons/svg/colored-icons/ic-colored-categorization-personal.svg';
import Button from '../../../../../generic-components/Button';

interface IProps {
  onSaveClick: (value: number) => void;
  isDisabled?: boolean;
  transaction: LiliTransaction;
}

const STYLES = {
  TOOLTIP_DIVIDER: 'h-[37px] w-[1px] bg-[#C5CCDC] mx-[6px]',
  TOOLTIP_WRAPPER: 'flex items-center justify-center w-full',
  TOOLTIP_TEXT: 'text-bw08 font-metric text-[20px] min-w-[30px]',
  SPLITTING_RESULT_BOX: 'min-w-[56px] flex items-center p-[5px] gap-[16px]',
  CURRENCY_INPUT: 'w-[100px] outline-none font-metricR text-[18px] text-action03',
};

export const WidgetSplitTransaction = ({ onSaveClick, isDisabled, transaction }: IProps) => {
  const [rangeValue, setRangeValue] = useState<number>(50);
  const [businessInputValue, setBusinessInputValue] = useState<string>('');
  const [personalInputValue, setPersonalInputValue] = useState<string>('');
  const businessPercentDisplay = Math.round(Number(rangeValue));
  const personalPercentDisplay = 100 - businessPercentDisplay;
  const triggerName = useRef<string>('');
  const transactionAmount = Math.abs(transaction.amount);
  const elPersonalInput = useRef<HTMLInputElement>(null);
  const elBusinessInput = useRef<HTMLInputElement>(null);
  const handleTransactionChange = () => {
    if (transaction.categoryDetails?.businessPercent) {
      const defaultRangeValue = 100 - 10 * transaction.categoryDetails.businessPercent;
      const personalValue = (defaultRangeValue * transactionAmount) / 100;
      setPersonalInputValue(personalValue.toFixed(2));
      setBusinessInputValue((transactionAmount - personalValue).toFixed(2));
    } else {
      setBusinessInputValue((transactionAmount / 2).toFixed(2));
      setPersonalInputValue((transactionAmount / 2).toFixed(2));
      setRangeValue(50);
    }
  };
  const handleAmountChange = (val: string | undefined, type: string) => {
    triggerName.current = 'input';
    if (type === 'BUSINESS') {
      if (!val) {
        setBusinessInputValue('0');
        setTimeout(() => {
          const length = elBusinessInput?.current?.value.length;
          elBusinessInput?.current?.setSelectionRange(length || 0, length || 0);
          elBusinessInput?.current?.focus();
        }, 50);
      } else {
        if (Number(val) > transactionAmount) return;
        setBusinessInputValue(val);
        setPersonalInputValue((transactionAmount - Number(val)).toFixed(2));
      }
    }
    if (type === 'PERSONAL') {
      if (!val) {
        setPersonalInputValue('0');
        setTimeout(() => {
          const length = elPersonalInput?.current?.value.length;
          elPersonalInput?.current?.setSelectionRange(length || 0, length || 0);
          elPersonalInput?.current?.focus();
        }, 50);
      } else {
        if (Number(val) > transactionAmount) return;
        setPersonalInputValue(val);
        setBusinessInputValue((transactionAmount - Number(val)).toFixed(2));
      }
    }
  };

  const handleOnSaveClick = () => {
    onSaveClick(rangeValue / 100);
  };

  useEffect(() => {
    if (triggerName.current === 'range') return;
    const numericValue = Number(personalInputValue);
    const percentFromTrxAmount = (numericValue / transactionAmount) * 100;
    setRangeValue(100 - percentFromTrxAmount);
  }, [personalInputValue]);

  useEffect(() => {
    if (triggerName.current === 'input') return;
    const businessValue = (rangeValue * transactionAmount) / 100;
    setBusinessInputValue(businessValue.toFixed(2));
    setPersonalInputValue((transactionAmount - businessValue).toFixed(2));
  }, [rangeValue]);

  useLayoutEffect(() => {
    handleTransactionChange();
  }, []);

  return (
    <div className='relative w-full h-full mt-[56px] flex flex-col items-center'>
      <div className='relative flex justify-center flex-col items-center'>
        <div className='w-[30px] h-[30px] z-[-1] rounded-[50%] absolute left-[50%] top-0 transform-gpu ml-[-18px]'>
          <div className='flex items-center justify-center w-fit absolute top-[-10px] left-[50%] translate-x-[-50%] translate-y-[-50px]'>
            <p
              className={`${STYLES.TOOLTIP_TEXT} text-center ${isDisabled ? 'opacity-50' : ''}`}
              data-testid='categorization-split-personal-percent'
            >
              {personalPercentDisplay}
            </p>
            <div className={STYLES.TOOLTIP_DIVIDER} />
            <p
              className={`${STYLES.TOOLTIP_TEXT} text-center ${isDisabled ? 'opacity-50' : ''}`}
              data-testid='categorization-split-business-percent'
            >
              {businessPercentDisplay}
            </p>
          </div>
        </div>
        <div className='relative flex justify-center flex-col items-center'>
          <CircleWrapper className='absolute left-0 top-[2px] z-50 h-[60px] w-[60px] translate-y-[-50%] translate-x-[-100%] bg-bw00'>
            <IconActionPersonal data-testid='categorization-split__icon-personal' />
          </CircleWrapper>
          <input
            disabled={isDisabled}
            value={rangeValue}
            min={0}
            max={100}
            step={1}
            id='splitTransactionRange'
            data-testid='categorization-split__input-range'
            className={`w-[190px]  h-[6px] ${isDisabled ? 'bg-bw04' : 'bg-action01'} appearance-none rounded-md cursor-pointer p-0`}
            type='range'
            onChange={(e) => {
              triggerName.current = 'range';
              setRangeValue(Number(e.target.value));
            }}
          />
          <CircleWrapper className='absolute right-0 top-[2px] z-50 h-[60px] w-[60px] translate-y-[-50%] translate-x-[100%] bg-bw00'>
            <IconActionBusiness data-testid='categorization-split__icon-business' />
          </CircleWrapper>
        </div>
      </div>
      <div className={`flex justify-between mt-[28px] mx-[12px] w-full ${isDisabled ? 'opacity-50' : ''}`}>
        <div className={STYLES.SPLITTING_RESULT_BOX}>
          <div className='flex flex-col'>
            <CurrencyInput
              ref={elPersonalInput}
              data-testid='categorization-split__personal-amount-input'
              disabled={isDisabled}
              onValueChange={(val) => handleAmountChange(val, 'PERSONAL')}
              prefix='$'
              value={personalInputValue}
              decimalsLimit={2}
              className={`${STYLES.CURRENCY_INPUT} text-left`}
            />
            <div className='w-full h-[1px] bg-action03' />
            <Heading
              title='Personal'
              className='font-metricR text-[14px] text-bw07 text-left'
              testId='categorization-split__personal-input-title'
            />
          </div>
        </div>
        <div className={STYLES.SPLITTING_RESULT_BOX}>
          <div className='flex flex-col'>
            <CurrencyInput
              ref={elBusinessInput}
              data-testid='categorization-split__business-amount-input'
              disabled={isDisabled}
              onValueChange={(val) => handleAmountChange(val, 'BUSINESS')}
              prefix='$'
              value={businessInputValue}
              decimalsLimit={2}
              className={`${STYLES.CURRENCY_INPUT} text-right`}
            />
            <div className='w-full h-[1px] bg-action03' />
            <Heading
              title='Business'
              className='font-metricR text-[14px] text-bw07 text-right'
              testId='categorization-split__business-input-title'
            />
          </div>
        </div>
      </div>
      <div>
        <Button
          title='Save'
          onClick={handleOnSaveClick}
          id='split-categorization-split-save-btn'
          testId='categorization-widget__split-save-btn'
          className='font-metricRMedium text-md text-action03 leading-6'
        />
      </div>
    </div>
  );
};
